import React, { useEffect, useMemo, useRef, useState } from 'react';
import { platesApi } from './api';
import { useExtractionPollingMany, usePlatesConsolidated } from './hooks';
import {
  PLATE_STATE_OPTIONS,
  FUEL_OPTIONS,
  VEHICLE_USE_OPTIONS,
  VEHICLE_TYPE_OPTIONS,
  normalizeVehicleType,
} from './platesConfig';
import ModalShell from '../insurance/ModalShell';

// Map document_type IA → tipo legacy del dropdown del admin.
function aiDocTypeToLegacy(dt) {
  if (dt === 'tarjeta_circulacion') return 'tarjeta';
  if (dt === 'placas') return 'placas';
  if (dt === 'acuse') return 'acuse';
  if (dt === 'unrelated') return 'otro';
  return null;
}

const MAX_BYTES = 25 * 1024 * 1024; // 25 MB

// pdf / jpg / png
const ACCEPT = 'application/pdf,image/jpeg,image/png';

const DOC_TYPES = [
  { value: 'tarjeta', label: 'Tarjeta de circulación' },
  { value: 'placas',  label: 'Fotos de placas'        },
  { value: 'acuse',   label: 'Acuse'                  },
  { value: 'otro',    label: 'Otro'                   },
];

// Cadencia de renovación por estado (meses).
const RENEWAL_BY_STATE = {
  CDMX: 60,
  EDOMEX: 60,
  Morelos: 12,
};

/* ──────────────────────────────────────────────────────────
 * Utilities
 * ────────────────────────────────────────────────────────── */

function detectTypeFromName(name = '') {
  const n = name.toLowerCase();
  if (n.includes('acuse')) return 'acuse';
  if (n.includes('placa')) return 'placas';
  if (n.includes('tarjeta') || n.includes('circulac')) return 'tarjeta';
  return 'tarjeta';
}

function isAcceptedFile(file) {
  const ok = ['application/pdf', 'image/jpeg', 'image/png'];
  if (ok.includes(file.type)) return true;
  const n = file.name.toLowerCase();
  return n.endsWith('.pdf') || n.endsWith('.jpg') || n.endsWith('.jpeg') || n.endsWith('.png');
}

function fileKind(file) {
  if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) return 'PDF';
  return 'IMG';
}

function toDateInputValue(iso) {
  if (!iso) return '';
  try { return new Date(iso).toISOString().slice(0, 10); }
  catch { return String(iso).slice(0, 10); }
}

function formatBytes(b) {
  if (!b && b !== 0) return '';
  const k = 1024;
  if (b < k) return b + ' B';
  if (b < k * k) return Math.round(b / k) + ' KB';
  return (b / (k * k)).toFixed(1) + ' MB';
}

function addMonths(isoDate, n) {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return '';
  d.setMonth(d.getMonth() + Number(n || 0));
  return d.toISOString().slice(0, 10);
}

/* ──────────────────────────────────────────────────────────
 * Componente principal
 * ────────────────────────────────────────────────────────── */

export default function UploadResponseModal({
  visible, onHide,
  qpId, view, toast, onCompleted,
}) {
  // sub-step: 'drop' | 'processing' | 'review'
  const [step, setStep] = useState('drop');
  const [files, setFiles] = useState([]); // [{id, file, nombre, size, type, kind, doc?:{id,...}, aiBadge:bool}]
  const [useAi, setUseAi] = useState(true);
  const [draggedOver, setDraggedOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [completing, setCompleting] = useState(false);

  // ── Datos de la tarjeta de circulación / placa ───────────
  const [plateNumber, setPlateNumber] = useState('');
  const [plateState, setPlateState] = useState(view?.estadoPlaca || '');
  const [folio, setFolio] = useState('');
  const [vinCard, setVinCard] = useState('');
  const [motorNumber, setMotorNumber] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [vehicleUse, setVehicleUse] = useState('');
  const [vehicleBrandCard, setVehicleBrandCard] = useState('');
  const [vehicleModelCard, setVehicleModelCard] = useState('');
  const [vehicleYearCard, setVehicleYearCard] = useState('');
  const [vehicleTypeCard, setVehicleTypeCard] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerRfc, setOwnerRfc] = useState('');
  const [issuedAt, setIssuedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [renewalCadence, setRenewalCadence] = useState(
    RENEWAL_BY_STATE[view?.estadoPlaca] || 60
  );
  // El admin tocó la cadencia manualmente → no la pisamos al cambiar el estado.
  const cadenceTouchedRef = useRef(false);

  // Datos AI consolidados que el admin ya prellenamos una sola vez.
  const prefilledRef = useRef(false);
  const fileInputRef = useRef(null);

  // ── Reset al cerrar ───────────────────────────────────────
  useEffect(() => {
    if (!visible) {
      setStep('drop');
      setFiles([]);
      setUseAi(true);
      setUploadResult(null);
      setPlateNumber('');
      setPlateState(view?.estadoPlaca || '');
      setFolio('');
      setVinCard('');
      setMotorNumber('');
      setFuelType('');
      setVehicleUse('');
      setVehicleBrandCard('');
      setVehicleModelCard('');
      setVehicleYearCard('');
      setVehicleTypeCard('');
      setOwnerName('');
      setOwnerRfc('');
      setIssuedAt('');
      setExpiresAt('');
      setRenewalCadence(RENEWAL_BY_STATE[view?.estadoPlaca] || 60);
      cadenceTouchedRef.current = false;
      prefilledRef.current = false;
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cadencia por estado (mientras el admin no la haya tocado) ──
  useEffect(() => {
    if (cadenceTouchedRef.current) return;
    const def = RENEWAL_BY_STATE[plateState];
    if (def != null) setRenewalCadence(def);
  }, [plateState]);

  // ── Si hay emisión + cadencia y no hay vencimiento, proyectarlo ──
  useEffect(() => {
    if (!issuedAt || !renewalCadence) return;
    setExpiresAt((curr) => curr || addMonths(issuedAt, renewalCadence));
  }, [issuedAt, renewalCadence]);

  // ── Documentos subidos + polling IA ───────────────────────
  const uploadedDocs = uploadResult?.documents ?? [];
  const docIds = useMemo(() => uploadedDocs.map((d) => d.id), [uploadedDocs]);

  // Polling múltiple: espera a que TODOS terminen su extracción.
  const pollingEnabled = useAi && docIds.length > 0 && (step === 'processing' || step === 'review');
  const { ready, items, anyFailed } = useExtractionPollingMany(docIds, pollingEnabled);

  // Snapshot consolidado (post-merge) cuando todos los docs terminaron.
  const { data: consolidated } = usePlatesConsolidated(qpId, useAi ? ready : false);

  // Banner IA por documento (low confidence / failed / unrelated).
  const aiBanners = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      const dt = it.extracted?.document_type ?? null;
      map[String(it.docId)] = {
        lowConfidence: it.status === 'done' && it.accuracy != null && it.accuracy < 30,
        failed: it.status === 'failed',
        accuracy: it.accuracy,
        documentType: dt,
        unrelated: dt === 'unrelated',
      };
    });
    return map;
  }, [items]);

  const anyLowConfidence = useMemo(
    () => Object.values(aiBanners).some((b) => b?.lowConfidence),
    [aiBanners]
  );

  // ── Pasar a review cuando todo terminó (o si no hay IA) ───
  useEffect(() => {
    if (step !== 'processing') return;
    if (!useAi && uploadResult) { setStep('review'); return; }
    if (useAi && ready) setStep('review');
  }, [step, useAi, uploadResult, ready]);

  // ── Auto-clasificar los archivos según lo que detectó la IA ──
  useEffect(() => {
    if (!useAi || items.length === 0) return;
    setFiles((prev) => prev.map((f) => {
      const it = items.find((x) => f.doc && String(x.docId) === String(f.doc.id));
      if (!it || it.status !== 'done') return f;
      const dt = it.extracted?.document_type ?? null;
      const legacy = aiDocTypeToLegacy(dt);
      if (!legacy) return f;
      return {
        ...f,
        type: legacy,
        aiDetected: true,
        aiUnrelated: dt === 'unrelated',
      };
    }));
  }, [items, useAi]);

  // ── Precarga UNA SOLA VEZ desde el snapshot consolidado ───
  useEffect(() => {
    if (!consolidated || prefilledRef.current) return;
    const card = consolidated.card || null;
    if (!card) { prefilledRef.current = true; return; }
    prefilledRef.current = true;

    setPlateNumber((c) => c || (card.plate_number || ''));
    // El estado emisor LEÍDO del documento manda sobre el default del sistema
    // (view.estadoPlaca, p.ej. "CDMX"). Si la IA no detectó estado, se conserva
    // el valor previo (default del sistema o lo que el admin haya puesto).
    setPlateState((c) => (card.plate_state || c || ''));
    setFolio((c) => c || (card.folio || ''));
    setVinCard((c) => c || (card.vin_card || ''));
    setMotorNumber((c) => c || (card.motor_number || ''));
    setFuelType((c) => c || (card.fuel_type || ''));
    setVehicleUse((c) => c || (card.vehicle_use || ''));
    setVehicleBrandCard((c) => c || (card.vehicle_brand_card || ''));
    setVehicleModelCard((c) => c || (card.vehicle_model_card || ''));
    setVehicleYearCard((c) => c || (card.vehicle_year_card != null ? String(card.vehicle_year_card) : ''));
    setVehicleTypeCard((c) => c || normalizeVehicleType(card.vehicle_type_card));
    setOwnerName((c) => c || (card.owner_name || ''));
    setOwnerRfc((c) => c || (card.owner_rfc || ''));
    setIssuedAt((c) => c || (card.issued_at ? toDateInputValue(card.issued_at) : ''));
    setExpiresAt((c) => c || (card.expires_at ? toDateInputValue(card.expires_at) : ''));
  }, [consolidated]);

  // Warnings globales (del snapshot consolidado).
  const globalWarnings = useMemo(
    () => (Array.isArray(consolidated?.warnings) ? consolidated.warnings : []),
    [consolidated]
  );

  // ── VIN del contrato vs VIN de la tarjeta (no bloquea) ────
  const contractVin = (view?.vin && view.vin !== '—') ? String(view.vin).trim() : '';
  const vinMismatch = !!(
    contractVin && vinCard &&
    contractVin.toUpperCase() !== String(vinCard).trim().toUpperCase()
  );

  // ── Validaciones ──────────────────────────────────────────
  const canComplete = (
    !!plateNumber.trim() &&
    uploadedDocs.length > 0 &&
    !completing
  );

  // ── Handlers de archivos ──────────────────────────────────
  const handleSelectFiles = (incoming) => {
    const list = Array.from(incoming || [])
      .filter(isAcceptedFile)
      .filter((file) => file.size <= MAX_BYTES)
      .map((file) => {
        const t = detectTypeFromName(file.name);
        return {
          id: 'f-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
          file,
          nombre: file.name,
          size: formatBytes(file.size),
          kind: fileKind(file),
          type: t,
          aiBadge: t === 'tarjeta' || t === 'placas',
        };
      });
    setFiles((prev) => [...prev, ...list]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDraggedOver(false);
    handleSelectFiles(e.dataTransfer?.files);
  };

  const processFiles = async () => {
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f, i) => {
        fd.append(`files[${i}][file]`, f.file);
        fd.append(`files[${i}][label]`, f.type);
      });
      fd.append('use_ai', useAi ? '1' : '0');

      const res = await platesApi.uploadPDFs(qpId, fd);
      if (res?.data?.success) {
        const docs = res.data.documents || [];
        setUploadResult({
          documents: docs,
          extraction: res.data.extraction || null,
        });
        // mapear local id ↔ doc id por orden
        setFiles((prev) => prev.map((f, i) => ({ ...f, doc: docs[i] || null })));
        setStep(useAi ? 'processing' : 'review');
      }
    } catch (e) {
      toast?.current?.show?.({
        severity: 'error',
        summary: 'Error al subir',
        detail: e?.response?.data?.message || e.message,
      });
    } finally {
      setUploading(false);
    }
  };

  // ── Submit final ─────────────────────────────────────────
  const handleComplete = async () => {
    if (!canComplete) return;
    setCompleting(true);
    try {
      const payload = {
        plate_number: plateNumber.trim(),
        plate_state: plateState || null,
        folio: folio || null,
        vin_card: vinCard || null,
        motor_number: motorNumber || null,
        fuel_type: fuelType || null,
        vehicle_use: vehicleUse || null,
        vehicle_brand_card: vehicleBrandCard || null,
        vehicle_model_card: vehicleModelCard || null,
        vehicle_year_card: vehicleYearCard ? parseInt(vehicleYearCard, 10) : null,
        vehicle_type_card: vehicleTypeCard || null,
        owner_name: ownerName || null,
        owner_rfc: ownerRfc || null,
        issued_at: issuedAt || null,
        expires_at: expiresAt || null,
        renewal_cadence_months: renewalCadence ? Number(renewalCadence) : null,
        // No mandar el placeholder de UI; el backend usa el gestor de config
        // (config('peicar.plates_gestor')) cuando no llega un nombre real.
        gestor_name: (view?.gestor?.nombre && view.gestor.nombre !== 'Gestor de placas') ? view.gestor.nombre : null,
        gestor_email: view?.gestor?.correo || null,
        document_ids: uploadedDocs.map((d) => d.id),
        ai_data: useAi ? (consolidated || null) : null,
        ai_status: useAi ? 'done' : 'disabled',
      };

      const res = await platesApi.complete(qpId, payload);
      if (res?.data?.success) {
        toast?.current?.show?.({
          severity: 'success',
          summary: '¡Placas registradas!',
          detail: 'Placa creada correctamente',
          life: 3500,
        });
        onCompleted?.();
        onHide?.();
      }
    } catch (e) {
      // 409 → Seguros no completado u otro conflicto de gating.
      const isConflict = e?.response?.status === 409;
      toast?.current?.show?.({
        severity: isConflict ? 'warn' : 'error',
        summary: isConflict ? 'No se puede completar todavía' : 'No se pudo completar',
        detail: e?.response?.data?.message
          || (isConflict ? 'Completa el paso de Seguros antes de tramitar las placas.' : e.message),
      });
    } finally {
      setCompleting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <ModalShell visible={visible} onClose={onHide} stacked width={760}>
      <button className="modal-close" onClick={onHide}><i className="pi pi-times" /></button>

      {(step === 'processing' || uploading) && (
        <div style={{ height: 3, background: 'var(--pei-gray-5)', position: 'relative', overflow: 'hidden' }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, height: '100%', width: '40%',
            background: 'var(--pei-gradient-cyan-green)',
            animation: 'seg-indeter 1.4s ease-in-out infinite',
          }} />
        </div>
      )}

      <div style={{ padding: '26px 32px 14px' }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Paso 6 · Placas</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.008em' }}>
          {step === 'drop' && 'Subir tarjeta de circulación y placas'}
          {step === 'processing' && 'Procesando tus archivos…'}
          {step === 'review' && 'Revisa los datos extraídos'}
        </div>
        <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4 }}>
          {view?.cliente} · {view?.vehiculo}
        </div>
      </div>

      <div className="scrollable" style={{ padding: '0 32px 14px', flex: 1, minHeight: 0 }}>
        {step === 'drop' && (
          <DropStep
            files={files}
            setFiles={setFiles}
            useAi={useAi}
            setUseAi={setUseAi}
            draggedOver={draggedOver}
            setDraggedOver={setDraggedOver}
            fileInputRef={fileInputRef}
            handleSelectFiles={handleSelectFiles}
            handleDrop={handleDrop}
          />
        )}

        {step === 'processing' && (
          <ProcessingStep files={files} useAi={useAi} aiBanners={aiBanners} />
        )}

        {step === 'review' && (
          <ReviewStep
            files={files}
            plateNumber={plateNumber} setPlateNumber={setPlateNumber}
            plateState={plateState} setPlateState={setPlateState}
            folio={folio} setFolio={setFolio}
            vinCard={vinCard} setVinCard={setVinCard}
            motorNumber={motorNumber} setMotorNumber={setMotorNumber}
            fuelType={fuelType} setFuelType={setFuelType}
            vehicleUse={vehicleUse} setVehicleUse={setVehicleUse}
            vehicleBrandCard={vehicleBrandCard} setVehicleBrandCard={setVehicleBrandCard}
            vehicleModelCard={vehicleModelCard} setVehicleModelCard={setVehicleModelCard}
            vehicleYearCard={vehicleYearCard} setVehicleYearCard={setVehicleYearCard}
            vehicleTypeCard={vehicleTypeCard} setVehicleTypeCard={setVehicleTypeCard}
            ownerName={ownerName} setOwnerName={setOwnerName}
            ownerRfc={ownerRfc} setOwnerRfc={setOwnerRfc}
            issuedAt={issuedAt} setIssuedAt={setIssuedAt}
            expiresAt={expiresAt} setExpiresAt={setExpiresAt}
            renewalCadence={renewalCadence}
            setRenewalCadence={(v) => { cadenceTouchedRef.current = true; setRenewalCadence(v); }}
            anyLowConfidence={anyLowConfidence}
            anyFailed={anyFailed}
            useAi={useAi}
            aiBanners={aiBanners}
            globalWarnings={globalWarnings}
            contractVin={contractVin}
            vinMismatch={vinMismatch}
          />
        )}
      </div>

      <div style={{ padding: '14px 32px 22px', borderTop: '1px solid var(--pei-gray-5)', display: 'flex', alignItems: 'center', gap: 10 }}>
        {step === 'drop' && (
          <>
            <button className="pei-btn pei-btn-ghost" onClick={onHide} disabled={uploading}>Cancelar</button>
            <div style={{ flex: 1 }} />
            <button className="pei-btn pei-btn-primary floor-shadow" disabled={files.length === 0 || uploading} onClick={processFiles}>
              {uploading ? 'Subiendo…' : useAi
                ? <>Procesar con IA <i className="pi pi-bolt" style={{ fontSize: 12 }} /></>
                : <>Continuar <i className="pi pi-arrow-right" style={{ fontSize: 12 }} /></>}
            </button>
          </>
        )}
        {step === 'processing' && (
          <>
            <button className="pei-btn pei-btn-ghost" disabled>Cancelar</button>
            <div style={{ flex: 1, fontSize: 12, color: 'var(--pei-text-helper)', textAlign: 'center' }}>
              No cierres la ventana
            </div>
            <button className="pei-btn pei-btn-ring" onClick={() => setStep('review')}>
              Saltar a revisar →
            </button>
          </>
        )}
        {step === 'review' && (
          <>
            <button className="pei-btn pei-btn-ghost" onClick={() => setStep('drop')}>← Volver</button>
            <div style={{ flex: 1, fontSize: 12, color: plateNumber.trim() ? 'var(--pei-green-true)' : 'var(--pei-text-helper)' }}>
              {plateNumber.trim()
                ? `Placa: ${plateNumber.trim()}${plateState ? ` · ${plateState}` : ''}`
                : 'Captura el número de placa para continuar'}
            </div>
            <button
              className="pei-btn pei-btn-primary floor-shadow"
              onClick={handleComplete}
              disabled={!canComplete}
            >
              <i className="pi pi-check" style={{ fontSize: 12 }} />
              {completing ? 'Completando…' : 'Completar paso'}
            </button>
          </>
        )}
      </div>
    </ModalShell>
  );
}

/* ──────────────────────────────────────────────────────────
 * Sub-componente: paso DROP
 * ────────────────────────────────────────────────────────── */
function DropStep({
  files, setFiles, useAi, setUseAi, draggedOver, setDraggedOver,
  fileInputRef, handleSelectFiles, handleDrop,
}) {
  return (
    <>
      <div
        onDragOver={(e) => { e.preventDefault(); setDraggedOver(true); }}
        onDragLeave={() => setDraggedOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${draggedOver ? 'var(--pei-green-bright)' : 'var(--pei-cyan-bright)'}`,
          background: draggedOver ? 'rgba(34,236,154,0.06)' : 'rgba(50,184,223,0.04)',
          borderRadius: 16,
          padding: files.length === 0 ? '54px 24px' : '20px 24px',
          textAlign: 'center',
          transition: 'all 220ms',
          cursor: 'pointer',
          marginBottom: 16,
        }}
      >
        <input ref={fileInputRef} type="file" multiple accept={ACCEPT}
          style={{ display: 'none' }} onChange={(e) => handleSelectFiles(e.target.files)} />
        {files.length === 0 ? (
          <>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(50,184,223,0.14)', margin: '0 auto 16px', display: 'grid', placeItems: 'center', color: 'var(--pei-cyan)' }}>
              <i className="pi pi-cloud-upload" style={{ fontSize: 24 }} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--pei-text)' }}>Arrastra los archivos aquí</div>
            <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4 }}>o haz click para seleccionar archivos</div>
            <div style={{ fontSize: 11, color: 'var(--pei-text-helper)', marginTop: 10 }}>PDF, JPG o PNG · máx 25 MB por archivo</div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', color: 'var(--pei-cyan)' }}>
            <i className="pi pi-cloud-upload" style={{ fontSize: 18 }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--pei-text-label)' }}>
              Arrastra más archivos o click para agregar
            </span>
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div className="pei-label" style={{ marginBottom: 10 }}>
            DOCUMENTOS · {files.length} archivo{files.length === 1 ? '' : 's'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {files.map(f => (
              <FileRow
                key={f.id}
                f={f}
                onTypeChange={(type) => setFiles(files.map(x => x.id === f.id ? { ...x, type } : x))}
                onRemove={() => setFiles(files.filter(x => x.id !== f.id))}
              />
            ))}
          </div>
        </div>
      )}

      <div style={{
        background: useAi ? 'rgba(34,236,154,0.08)' : 'var(--pei-bg-soft)',
        border: `1.5px solid ${useAi ? 'rgba(34,236,154,0.40)' : 'var(--pei-gray-5)'}`,
        borderRadius: 14,
        padding: '14px 16px',
        display: 'flex', alignItems: 'center', gap: 14,
        transition: 'all 220ms',
      }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10,
          background: useAi ? 'var(--pei-gradient-mint-cyan)' : 'var(--pei-gray-5)',
          color: 'white',
          display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <i className="pi pi-bolt" style={{ fontSize: 16 }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--pei-text)' }}>Usar IA para extraer datos</div>
          <div style={{ fontSize: 12, color: 'var(--pei-text-helper)', marginTop: 2 }}>
            Lee placa, estado, VIN, vigencia y datos del propietario de la tarjeta en ~15 segundos.
          </div>
        </div>
        <Toggle value={useAi} onChange={setUseAi} />
      </div>
    </>
  );
}

function FileRow({ f, onTypeChange, onRemove }) {
  const isUnrelated = !!f.aiUnrelated;
  const isImg = f.kind === 'IMG';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 14px 10px 10px',
      background: isUnrelated ? 'var(--pei-bg-soft)' : 'white',
      border: '1px solid var(--pei-gray-5)',
      borderRadius: 12,
      flexWrap: 'wrap',
      opacity: isUnrelated ? 0.7 : 1,
    }}>
      <div style={{
        width: 40, height: 52, borderRadius: 6,
        background: isImg ? '#eff6ff' : '#fff5f5',
        border: `1px solid ${isImg ? '#bfdbfe' : '#fecaca'}`,
        display: 'grid', placeItems: 'center',
        color: isImg ? 'var(--pei-cyan)' : 'var(--pei-danger)',
        flexShrink: 0,
        fontSize: 10, fontWeight: 700,
        letterSpacing: '0.05em',
      }}>
        {f.kind}
      </div>
      <div style={{ flex: 1, minWidth: 160 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--pei-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 6 }}>
          {f.nombre}
          {f.aiDetected && !isUnrelated && (
            <span title="Detectado por IA" style={{ fontSize: 10, padding: '2px 6px', background: 'rgba(34,236,154,0.20)', color: '#0a7c4c', borderRadius: 999, fontWeight: 700, letterSpacing: '0.04em' }}>
              <i className="pi pi-bolt" style={{ fontSize: 9, marginRight: 3 }} /> detectado
            </span>
          )}
          {isUnrelated && (
            <span title="La IA detectó que no es tarjeta de circulación, placas ni acuse" style={{ fontSize: 10, padding: '2px 6px', background: 'var(--pei-gray-5)', color: 'var(--pei-text-helper)', borderRadius: 999, fontWeight: 700, letterSpacing: '0.04em' }}>
              ajeno
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--pei-text-helper)', marginTop: 2 }}>{f.size}</div>
        {isUnrelated && (
          <div style={{ fontSize: 11, color: 'var(--pei-text-helper)', marginTop: 4, fontStyle: 'italic' }}>
            Este documento no parece ser tarjeta de circulación, fotos de placas ni acuse.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--pei-text-label)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tipo</span>
        <select
          value={f.type}
          onChange={(e) => onTypeChange(e.target.value)}
          style={{
            height: 32, padding: '0 8px',
            border: '1.5px solid var(--pei-cyan-bright)',
            borderRadius: 8, fontSize: 13, fontFamily: 'inherit',
            background: 'white', color: 'var(--pei-text)', cursor: 'pointer', outline: 'none',
          }}
        >
          {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      <button onClick={onRemove} style={{
        width: 32, height: 32, borderRadius: 8,
        background: 'transparent', border: 'none',
        color: 'var(--pei-text-helper)', cursor: 'pointer',
        display: 'grid', placeItems: 'center',
      }} title="Quitar archivo">
        <i className="pi pi-trash" style={{ fontSize: 13 }} />
      </button>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button onClick={() => onChange(!value)} type="button" style={{
      width: 42, height: 24, borderRadius: 12,
      background: value ? 'var(--pei-green-bright)' : 'var(--pei-gray-3)',
      border: 'none', cursor: 'pointer',
      position: 'relative',
      transition: 'background 200ms',
      flexShrink: 0,
    }}>
      <span style={{
        position: 'absolute', top: 2, left: value ? 20 : 2,
        width: 20, height: 20, borderRadius: 10,
        background: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
        transition: 'left 200ms',
      }} />
    </button>
  );
}

/* ──────────────────────────────────────────────────────────
 * Sub-componente: PROCESSING
 * ────────────────────────────────────────────────────────── */
function ProcessingStep({ files, useAi, aiBanners }) {
  return (
    <>
      <div className="banner info" style={{ marginBottom: 18 }}>
        <i className="pi pi-info-circle" />
        <div>
          <div className="b-title">Esto suele tardar 10–20 segundos</div>
          <div className="b-sub">Los datos extraídos serán editables antes de completar el paso.</div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {files.map(f => {
          const docId = f.doc?.id ? String(f.doc.id) : null;
          const banner = docId ? aiBanners[docId] : null;
          const done = banner && (banner.failed || banner.accuracy != null);
          const isImg = f.kind === 'IMG';
          return (
            <div key={f.id} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '14px 16px',
              background: 'white',
              border: '1px solid var(--pei-gray-5)',
              borderRadius: 14,
            }}>
              <div style={{ position: 'relative', width: 40, height: 52, flexShrink: 0 }}>
                <div style={{
                  width: 40, height: 52, borderRadius: 6,
                  background: isImg ? '#eff6ff' : '#fff5f5',
                  border: `1px solid ${isImg ? '#bfdbfe' : '#fecaca'}`,
                  display: 'grid', placeItems: 'center',
                  color: isImg ? 'var(--pei-cyan)' : 'var(--pei-danger)',
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                }}>{f.kind}</div>
                {!done && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.85)', display: 'grid', placeItems: 'center', borderRadius: 6 }}>
                    <div className="spinner-cyan" />
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--pei-text)' }}>{f.nombre}</div>
                <div style={{ fontSize: 12, color: done ? 'var(--pei-green-true)' : 'var(--pei-cyan)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className={`pi ${done ? 'pi-check' : 'pi-bolt'}`} style={{ fontSize: 11 }} />
                  {!useAi ? 'Subiendo archivo…' : (done
                    ? (banner.failed ? 'IA no procesó este documento' : `IA terminó · ${banner.accuracy ?? '—'}%`)
                    : 'Extrayendo datos…')}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ──────────────────────────────────────────────────────────
 * Sub-componente: REVIEW
 * ────────────────────────────────────────────────────────── */
function ReviewStep(props) {
  const {
    files,
    plateNumber, setPlateNumber,
    plateState, setPlateState,
    folio, setFolio,
    vinCard, setVinCard,
    motorNumber, setMotorNumber,
    fuelType, setFuelType,
    vehicleUse, setVehicleUse,
    vehicleBrandCard, setVehicleBrandCard,
    vehicleModelCard, setVehicleModelCard,
    vehicleYearCard, setVehicleYearCard,
    vehicleTypeCard, setVehicleTypeCard,
    ownerName, setOwnerName,
    ownerRfc, setOwnerRfc,
    issuedAt, setIssuedAt,
    expiresAt, setExpiresAt,
    renewalCadence, setRenewalCadence,
    anyLowConfidence, anyFailed, useAi, aiBanners,
    globalWarnings, contractVin, vinMismatch,
  } = props;

  // Lista de docs marcados como unrelated (para mostrar mensaje)
  const unrelatedFiles = files.filter((f) => f.aiUnrelated);

  // Banner global IA: si algún doc quedó con accuracy < 30 o failed
  const showAiWarning = useAi && (anyLowConfidence || anyFailed || Object.values(aiBanners).some((b) => b?.failed));

  return (
    <>
      {showAiWarning && (
        <div className="banner warning" style={{ marginBottom: 18 }}>
          <i className="pi pi-exclamation-triangle" />
          <div>
            <div className="b-title">La IA no leyó alguno de los documentos con suficiente confianza</div>
            <div className="b-sub">Revisa cada campo antes de guardar. No bloqueamos el envío.</div>
          </div>
        </div>
      )}

      {Array.isArray(globalWarnings) && globalWarnings.length > 0 && (
        <div className="banner warning" style={{ marginBottom: 18 }}>
          <i className="pi pi-exclamation-triangle" />
          <div>
            <div className="b-title">Avisos de la extracción IA</div>
            <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 12, color: 'var(--pei-text-helper)' }}>
              {globalWarnings.slice(0, 6).map((w, i) => (<li key={i}>{w}</li>))}
              {globalWarnings.length > 6 && (
                <li>… y {globalWarnings.length - 6} más</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {vinMismatch && (
        <div className="banner warning" style={{ marginBottom: 18 }}>
          <i className="pi pi-exclamation-triangle" />
          <div>
            <div className="b-title">El VIN de la tarjeta no coincide con el del contrato</div>
            <div className="b-sub">
              Contrato: <strong>{contractVin}</strong> · Tarjeta: <strong>{vinCard}</strong>. Verifica antes de completar (no bloqueamos el envío).
            </div>
          </div>
        </div>
      )}

      {unrelatedFiles.length > 0 && (
        <div className="banner info" style={{ marginBottom: 18, opacity: 0.85 }}>
          <i className="pi pi-info-circle" />
          <div>
            <div className="b-title">{unrelatedFiles.length} documento{unrelatedFiles.length === 1 ? '' : 's'} no parece relacionado con las placas</div>
            <div className="b-sub">
              ({unrelatedFiles.map((f) => f.nombre).join(', ')})
            </div>
          </div>
        </div>
      )}

      {/* Datos de la tarjeta de circulación */}
      <div className="pei-label" style={{ marginBottom: 10 }}>DATOS DE LA TARJETA DE CIRCULACIÓN</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <EditableField label="N° de placa" value={plateNumber} onChange={setPlateNumber} mono required />
        <SelectField label="Estado emisor" value={plateState} onChange={setPlateState} options={PLATE_STATE_OPTIONS} />
        <EditableField label="Folio" value={folio} onChange={setFolio} mono />
        <EditableField label="VIN (tarjeta)" value={vinCard} onChange={setVinCard} mono warn={vinMismatch} />
        <EditableField label="N° de motor" value={motorNumber} onChange={setMotorNumber} mono />
        <SelectField label="Combustible" value={fuelType} onChange={setFuelType} options={FUEL_OPTIONS} />
        <SelectField label="Uso" value={vehicleUse} onChange={setVehicleUse} options={VEHICLE_USE_OPTIONS} />
        <SelectField label="Tipo de vehículo" value={vehicleTypeCard} onChange={setVehicleTypeCard} options={VEHICLE_TYPE_OPTIONS} />
        <EditableField label="Marca (tarjeta)" value={vehicleBrandCard} onChange={setVehicleBrandCard} />
        <EditableField label="Modelo (tarjeta)" value={vehicleModelCard} onChange={setVehicleModelCard} />
        <EditableField label="Año (tarjeta)" value={vehicleYearCard} onChange={(v) => setVehicleYearCard(v.replace(/[^\d]/g, '').slice(0, 4))} type="number" />
      </div>

      {/* Propietario */}
      <div className="pei-label" style={{ marginTop: 22, marginBottom: 10 }}>PROPIETARIO</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <EditableField label="Nombre del propietario" value={ownerName} onChange={setOwnerName} />
        <EditableField label="RFC del propietario" value={ownerRfc} onChange={setOwnerRfc} mono />
      </div>

      {/* Vigencia */}
      <div className="pei-label" style={{ marginTop: 22, marginBottom: 10 }}>VIGENCIA</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
        <EditableField label="Emisión" value={issuedAt} onChange={setIssuedAt} type="date" />
        <EditableField label="Vencimiento" value={expiresAt} onChange={setExpiresAt} type="date" />
        <EditableField
          label="Renovación (meses)"
          value={String(renewalCadence ?? '')}
          onChange={(v) => setRenewalCadence(Math.max(0, parseInt(v, 10) || 0))}
          type="number"
        />
      </div>

      {/* Pie informativo: la placa es la fuente de verdad */}
      <div style={{
        marginTop: 16,
        padding: '12px 16px',
        background: plateNumber.trim() ? 'rgba(34,236,154,0.10)' : 'var(--pei-bg-soft)',
        border: `1px solid ${plateNumber.trim() ? 'rgba(34,236,154,0.30)' : 'var(--pei-gray-5)'}`,
        borderRadius: 12,
        display: 'flex', alignItems: 'center', gap: 10,
        fontSize: 13,
      }}>
        <i className={`pi ${plateNumber.trim() ? 'pi-check-circle' : 'pi-info-circle'}`}
          style={{ fontSize: 14, color: plateNumber.trim() ? 'var(--pei-green-true)' : 'var(--pei-text-helper)' }} />
        <div>
          {plateNumber.trim()
            ? <span><strong>Placa {plateNumber.trim()}</strong>{plateState ? ` · ${plateState}` : ''} lista para registrar.</span>
            : <span>El número de placa es obligatorio para completar el paso.</span>}
        </div>
      </div>
    </>
  );
}

function EditableField({ label, value, onChange, placeholder, mono, type = 'text', warn, required }) {
  const isEmpty = value == null || value === '' || value === '—';
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, minHeight: 16 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--pei-text-label)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {label}{required && isEmpty && <span style={{ color: 'var(--pei-danger)', marginLeft: 4 }}>*</span>}
        </span>
      </div>
      <input
        type={type}
        value={value == null ? '' : value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Capturar…'}
        style={{
          width: '100%', height: 38, padding: '0 12px',
          border: `1.5px solid ${warn ? '#FCD34D' : 'var(--pei-cyan-bright)'}`,
          borderRadius: 10,
          background: warn ? 'rgba(252,211,77,0.10)' : 'white',
          fontFamily: mono ? 'var(--pei-font-mono)' : 'inherit',
          fontSize: mono ? 13 : 14,
          fontWeight: 500,
          color: 'var(--pei-text)',
          outline: 'none',
        }}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <div style={{ marginBottom: 6, fontSize: 11, fontWeight: 600, color: 'var(--pei-text-label)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {label}
      </div>
      <select value={value || ''} onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', height: 38, padding: '0 12px',
          border: '1.5px solid var(--pei-cyan-bright)',
          borderRadius: 10, fontSize: 14, fontFamily: 'inherit',
          background: 'white', color: 'var(--pei-text)', cursor: 'pointer', outline: 'none',
        }}>
        <option value="">Selecciona…</option>
        {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  );
}
